import { getApiError } from '@/utils/apiError';

export interface BatchFailure { label: string; message: string }
export interface BatchResult { succeeded: string[]; failures: BatchFailure[] }

/** Lý do lỗi từ BE (`response.data.error.message` hoặc `response.data.message`); lỗi không đến từ API → `fallback`. */
export function errorMessageOf(error: unknown, fallback: string): string {
  if (!(error as { response?: unknown } | null)?.response) return fallback;
  return getApiError(error)?.message?.trim() || fallback;
}

/** TCR-2264 — gọi API từng đơn, tối đa `limit` song song; lỗi 1 đơn không chặn các đơn khác. */
export async function runBatch<T extends { _id: string }>(
  items: T[],
  limit: number,
  label: (item: T) => string,
  run: (item: T) => Promise<unknown>,
): Promise<BatchResult> {
  const succeeded: string[] = [];
  const failures: BatchFailure[] = [];
  const outcome: Array<boolean | undefined> = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      const item = items[index];
      try {
        await run(item);
        outcome[index] = true;
      } catch (error) {
        outcome[index] = false;
        failures.push({ label: label(item), message: errorMessageOf(error, 'Thao tác thất bại') });
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  items.forEach((item, index) => { if (outcome[index]) succeeded.push(item._id); });
  return { succeeded, failures };
}

/**
 * TCR-2264 — API tạo phiếu xuất (Đang đóng gói) chạy 1 lệnh cập nhật hàng loạt và trả HTTP 200 kèm
 * `failed_count` (không nêu đơn nào): đơn đã đổi trạng thái giữa lúc chọn và lúc bấm bị bỏ qua im lặng.
 */
export function packingResultFailures(result: { success_count: number; failed_count: number }): BatchFailure[] {
  if (!result.failed_count) return [];
  return [{
    label: `${result.failed_count} đơn`,
    message: 'Không chuyển được sang Đang đóng gói — đơn đã đổi trạng thái hoặc không còn ở Chờ đóng gói',
  }];
}

export function formatBatchFailures(failures: BatchFailure[], max = 5): string[] {
  const lines = failures.slice(0, max).map((failure) => `${failure.label}: ${failure.message}`);
  if (failures.length > max) lines.push(`và ${failures.length - max} đơn khác`);
  return lines;
}
